import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Wrench, X, CheckCircle, Clock, ChatCircleText, Warning } from "@phosphor-icons/react";
import { fmtDuration } from "../lib/duration";

/**
 * Shift Millwright — Breakdown Notifications widget.
 *
 * Floating bell button on the header. When a Grindrod-accountable vessel
 * breakdown is logged via the Pause modal, a notification is raised here.
 * The on-shift millwright must ACCEPT (acknowledge) the alert as confirmation
 * that maintenance is attending. They can also revise the ETA and post
 * feedback updates while attending.
 *
 * Visible to: shift_millwright, manager, admin, plant_manager.
 */
const ELIGIBLE = new Set(["shift_millwright", "manager", "admin", "plant_manager"]);

export default function MillwrightAlerts() {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [audioOn] = useState(() => {
        try { return sessionStorage.getItem("grindrod_alerts_muted") !== "1"; } catch { return true; }
    });
    const eligible = user && ELIGIBLE.has(user.role);

    const load = useCallback(async () => {
        if (!eligible) return;
        try {
            const [a, b] = await Promise.all([
                api.get("/millwright/breakdown-notifications", { params: { limit: 30 } }),
                api.get("/millwright/breakdown-notifications/unread/count"),
            ]);
            setItems(a.data || []);
            setUnread(b.data?.count || 0);
        } catch (err) {
            console.warn("Millwright load failed:", err?.message || err);
        }
    }, [eligible]);

    useEffect(() => {
        if (!eligible) return;
        load();
        const t = setInterval(load, 15000);
        return () => clearInterval(t);
    }, [load, eligible]);

    // Audible chime + browser notification when a new open breakdown is detected
    const [seenIds, setSeenIds] = useState(new Set());
    useEffect(() => {
        if (!eligible) return;
        const openItems = items.filter((n) => n.status === "open");
        const fresh = openItems.filter((n) => !seenIds.has(n.id));
        if (fresh.length === 0) return;
        const next = new Set(seenIds);
        fresh.forEach((n) => next.add(n.id));
        setSeenIds(next);
        // Only play sound after the first load — avoid blasting on page open
        if (seenIds.size === 0) return;
        if (audioOn) {
            try {
                // Short synthesized chime
                // eslint-disable-next-line no-undef
                const ctx = new (window.AudioContext || window.webkitAudioContext)();
                const o = ctx.createOscillator();
                const g = ctx.createGain();
                o.connect(g); g.connect(ctx.destination);
                o.type = "triangle";
                o.frequency.setValueAtTime(880, ctx.currentTime);
                g.gain.setValueAtTime(0.001, ctx.currentTime);
                g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.05);
                g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.5);
                o.start();
                o.stop(ctx.currentTime + 0.55);
            } catch { /* ignore */ }
        }
        // Browser notification (only if granted)
        try {
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
                fresh.forEach((n) => new Notification("Grindrod Breakdown", {
                    body: `${n.conveyor_name || "Conveyor"} — ${n.reason}: ${n.description || ""}`.slice(0, 140),
                    tag: n.id,
                }));
            }
        } catch { /* ignore */ }
    }, [items, eligible, audioOn, seenIds]);

    // One-time permission prompt
    useEffect(() => {
        if (!eligible) return;
        try {
            if (typeof Notification !== "undefined" && Notification.permission === "default") {
                Notification.requestPermission().catch(() => {});
            }
        } catch { /* ignore */ }
    }, [eligible]);

    if (!eligible) return null;

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className={`relative inline-flex items-center justify-center w-10 h-10 rounded-sm transition-colors ${unread > 0 ? "bg-[var(--grindrod-red)] text-white animate-pulse-slow" : "bg-white/10 hover:bg-white/20 text-white"}`}
                title="Maintenance alerts"
                data-testid="millwright-bell-btn"
                aria-label="Maintenance alerts"
            >
                <Wrench size={18} weight="fill" />
                {unread > 0 && (
                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] inline-flex items-center justify-center rounded-full bg-yellow-400 text-[var(--grindrod-navy)] text-[10px] font-bold tabular px-1" data-testid="millwright-bell-badge">
                        {unread}
                    </span>
                )}
            </button>

            {open && (
                <MillwrightAlertsPanel
                    items={items}
                    onClose={() => setOpen(false)}
                    onChanged={load}
                    user={user}
                />
            )}
        </>
    );
}


function MillwrightAlertsPanel({ items, onClose, onChanged, user }) {
    const isMillwright = user?.role === "shift_millwright";
    const groups = useMemo(() => ({
        open: items.filter((i) => i.status === "open"),
        acknowledged: items.filter((i) => i.status === "acknowledged"),
        closed: items.filter((i) => i.status === "closed").slice(0, 10),
    }), [items]);
    const [active, setActive] = useState(null); // notification currently being acted on

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="millwright-panel">
            <div
                className="g-card w-full max-w-2xl overflow-hidden my-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="g-card-header-red">
                    <Wrench size={18} weight="fill" />
                    <div className="flex-1">
                        <div className="uppercase-label text-[9px] text-white/70">Maintenance Alerts</div>
                        <div className="font-display font-bold text-white">Grindrod Breakdowns</div>
                    </div>
                    <span className="text-white/80 text-xs font-semibold mr-2 tabular">
                        {groups.open.length} open · {groups.acknowledged.length} attending
                    </span>
                    <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="millwright-panel-close" aria-label="Close">
                        <X size={18} weight="bold" />
                    </button>
                </div>

                <div className="max-h-[70vh] overflow-y-auto">
                    {items.length === 0 ? (
                        <div className="p-10 text-center text-slate-400 text-sm" data-testid="millwright-empty">
                            <CheckCircle size={32} weight="fill" className="mx-auto text-[var(--grindrod-green)]" />
                            <div className="mt-2">No active maintenance alerts.</div>
                        </div>
                    ) : (
                        <>
                            <Section label="OPEN — needs acceptance" items={groups.open} accent="var(--grindrod-red)" onSelect={setActive} user={user} />
                            <Section label="Acknowledged — maintenance attending" items={groups.acknowledged} accent="#FFB300" onSelect={setActive} user={user} />
                            <Section label="Recently closed" items={groups.closed} accent="#94A3B8" onSelect={setActive} user={user} muted />
                        </>
                    )}
                </div>

                {!isMillwright && groups.open.length > 0 && (
                    <div className="px-4 py-2.5 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-100" data-testid="millwright-read-only-note">
                        Read-only view — shift millwright must accept to confirm maintenance is on the way.
                    </div>
                )}
            </div>

            {active && (
                <AlertDetailModal
                    notification={active}
                    onClose={() => setActive(null)}
                    onChanged={onChanged}
                    user={user}
                />
            )}
        </div>
    );
}


function Section({ label, items, accent, onSelect, user, muted }) {
    if (!items || items.length === 0) return null;
    return (
        <div data-testid={`millwright-section-${label.replace(/\W+/g, "-").toLowerCase()}`}>
            <div className="px-4 py-2 bg-slate-50 border-y border-slate-100 uppercase-label text-[10px] flex items-center gap-2" style={{ color: accent }}>
                <span className="inline-block w-2 h-2 rounded-full" style={{ background: accent }} />
                {label}
                <span className="ml-auto tabular text-slate-400">{items.length}</span>
            </div>
            <ul className="divide-y divide-slate-100">
                {items.map((n) => (
                    <NotificationRow key={n.id} n={n} onSelect={onSelect} user={user} muted={muted} />
                ))}
            </ul>
        </div>
    );
}


function NotificationRow({ n, onSelect, user, muted }) {
    const ageMin = Math.max(0, Math.round((Date.now() - new Date(n.raised_at).getTime()) / 60000));
    const isMillwright = user?.role === "shift_millwright";
    const canAck = isMillwright && n.status === "open";
    return (
        <li className={`px-4 py-3 flex items-start gap-3 hover:bg-slate-50 ${muted ? "opacity-70" : ""}`} data-testid={`millwright-row-${n.id}`}>
            <div className="mt-0.5">
                {n.status === "open" && <Warning size={20} weight="fill" className="text-[var(--grindrod-red)]" />}
                {n.status === "acknowledged" && <Clock size={20} weight="fill" className="text-[#FFB300]" />}
                {n.status === "closed" && <CheckCircle size={20} weight="fill" className="text-[var(--grindrod-green)]" />}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display font-bold text-sm text-[var(--grindrod-navy)] tabular">{n.conveyor_name || "Conveyor"}</span>
                    <span className="text-xs text-slate-500">·</span>
                    <span className="text-xs text-slate-600">{n.vessel_name}</span>
                    {n.status === "open" && (
                        <span className="g-pill g-pill-red animate-pulse" style={{ fontSize: 9, padding: "1px 6px" }}>NEEDS ACCEPT</span>
                    )}
                    {n.status === "acknowledged" && (
                        <span className="g-pill" style={{ fontSize: 9, padding: "1px 6px", background: "#FEF3C7", color: "#92400E", border: "1px solid #FFB300" }}>
                            ON THE WAY · {n.acknowledged_by_name}
                        </span>
                    )}
                </div>
                <div className="text-sm text-slate-700 mt-0.5 line-clamp-2">{n.description || n.reason}</div>
                <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                    <span>Raised {fmtDuration(ageMin)} ago by {n.raised_by_name}</span>
                    {n.eta_minutes != null && (
                        <>
                            <span>·</span>
                            <span>ETA <span className="tabular font-semibold">{fmtDuration(n.eta_minutes)}</span></span>
                        </>
                    )}
                    {n.feedback && n.feedback.length > 0 && (
                        <>
                            <span>·</span>
                            <span className="inline-flex items-center gap-1"><ChatCircleText size={12} /> {n.feedback.length}</span>
                        </>
                    )}
                </div>
            </div>
            <button
                onClick={() => onSelect(n)}
                className={`btn-outline !py-1 !px-2.5 text-[11px] whitespace-nowrap ${canAck ? "!border-[var(--grindrod-red)] !text-[var(--grindrod-red)] font-bold" : ""}`}
                data-testid={`millwright-open-${n.id}`}
            >
                {canAck ? "Accept" : "Open"}
            </button>
        </li>
    );
}


function AlertDetailModal({ notification, onClose, onChanged, user }) {
    const [n, setN] = useState(notification);
    const [eta, setEta] = useState(n.eta_minutes != null ? String(n.eta_minutes) : "");
    const [feedback, setFeedback] = useState("");
    const [busy, setBusy] = useState(false);
    const isMillwright = user?.role === "shift_millwright";
    const canEdit = ["shift_millwright", "manager", "admin", "plant_manager"].includes(user?.role) && n.status !== "closed";
    const canMarkReady = isMillwright && (n.status === "acknowledged" || n.status === "open") && !n.ready_to_start;

    const reload = async () => {
        try {
            const r = await api.get(`/millwright/breakdown-notifications/${n.id}`);
            setN(r.data);
        } catch (err) {
            console.warn("Reload notification failed:", err?.message || err);
        }
    };

    const accept = async () => {
        setBusy(true);
        try {
            await api.post(`/millwright/breakdown-notifications/${n.id}/acknowledge`, { note: "" });
            await reload();
            onChanged && onChanged();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed to accept");
        } finally {
            setBusy(false);
        }
    };

    const markReady = async () => {
        if (!window.confirm(
            "Confirm the breakdown is fixed and the vessel is ready to resume loading?\n\n" +
            "The supervisor will be notified — they will resume the vessel."
        )) return;
        setBusy(true);
        try {
            await api.post(`/millwright/breakdown-notifications/${n.id}/ready`, { note: "" });
            onChanged && onChanged();
            onClose && onClose();     // terminal step — close the detail modal
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed to mark ready");
        } finally {
            setBusy(false);
        }
    };

    const saveEta = async () => {
        const v = parseInt(eta, 10);
        if (Number.isNaN(v) || v < 0) {
            alert("Enter a valid ETA in minutes");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/millwright/breakdown-notifications/${n.id}/eta`, { eta_minutes: v });
            await reload();
            onChanged && onChanged();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed to update ETA");
        } finally {
            setBusy(false);
        }
    };

    const sendFeedback = async () => {
        if (!feedback.trim()) return;
        setBusy(true);
        try {
            await api.post(`/millwright/breakdown-notifications/${n.id}/feedback`, { text: feedback.trim() });
            setFeedback("");
            await reload();
            onChanged && onChanged();
        } catch (err) {
            alert(err?.response?.data?.detail || "Failed to send feedback");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="millwright-detail-modal">
            <div className="g-card w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="g-card-header-red">
                    <Warning size={18} weight="fill" />
                    <div className="flex-1">
                        <div className="uppercase-label text-[9px] text-white/70">Breakdown · {n.status}</div>
                        <div className="font-display font-bold text-white">{n.conveyor_name || "Conveyor"} · {n.vessel_name}</div>
                    </div>
                    <button onClick={onClose} className="text-white/80 hover:text-white" data-testid="millwright-detail-close" aria-label="Close">
                        <X size={18} weight="bold" />
                    </button>
                </div>

                <div className="overflow-y-auto p-5 space-y-4">
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-3">
                        <div className="uppercase-label text-[10px] text-slate-500 font-semibold">What happened</div>
                        <div className="text-sm text-[var(--grindrod-navy)] font-semibold">{n.reason}</div>
                        <div className="text-sm text-slate-700 mt-1">{n.description || "—"}</div>
                        <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-2 flex-wrap">
                            <span>Raised by <strong>{n.raised_by_name}</strong></span>
                            <span>·</span>
                            <span className="tabular">{new Date(n.raised_at).toLocaleString()}</span>
                            {n.acknowledged_by_name && (
                                <>
                                    <span>·</span>
                                    <span>Accepted by <strong>{n.acknowledged_by_name}</strong></span>
                                </>
                            )}
                        </div>
                    </div>

                    {n.status === "open" && isMillwright && (
                        <button
                            onClick={accept}
                            disabled={busy}
                            className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-40"
                            data-testid="millwright-accept-btn"
                        >
                            <CheckCircle size={16} weight="fill" /> Accept — Maintenance is on the way
                        </button>
                    )}

                    {canMarkReady && (
                        <button
                            onClick={markReady}
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 bg-[var(--grindrod-green)] hover:opacity-90 text-white font-display font-bold uppercase tracking-wide text-sm px-4 py-3 rounded-sm disabled:opacity-40"
                            data-testid="millwright-ready-btn"
                        >
                            <CheckCircle size={16} weight="fill" /> Ready to start — notify supervisor
                        </button>
                    )}

                    {n.ready_to_start && n.status !== "closed" && (
                        <div className="bg-[var(--grindrod-green-bg)] border border-[var(--grindrod-green)] rounded-sm px-3 py-2 text-sm" data-testid="ready-pill">
                            <div className="font-display font-bold text-[var(--grindrod-green)] uppercase tracking-wide text-xs">
                                Ready to start
                            </div>
                            <div className="text-slate-700 mt-0.5">
                                <strong>{n.ready_by_name}</strong> signalled the vessel is ready at <span className="tabular">{n.ready_at ? new Date(n.ready_at).toLocaleTimeString() : ""}</span>. Awaiting supervisor to resume loading.
                            </div>
                        </div>
                    )}

                    {canEdit && (
                        <div>
                            <label className="uppercase-label block mb-1.5 text-[10px] text-slate-500 font-semibold">Revise ETA (minutes)</label>
                            <div className="flex gap-2">
                                <input
                                    type="number"
                                    min="0"
                                    value={eta}
                                    onChange={(e) => setEta(e.target.value)}
                                    className="g-input flex-1 tabular"
                                    placeholder="e.g. 45"
                                    data-testid="millwright-eta-input"
                                />
                                <button onClick={saveEta} disabled={busy} className="btn-outline text-xs disabled:opacity-40" data-testid="millwright-eta-save-btn">
                                    Update ETA
                                </button>
                            </div>
                            <div className="text-[11px] text-slate-500 mt-1">
                                Pushes the new ETA to the live vessel pause card.
                            </div>
                        </div>
                    )}

                    {canEdit && (
                        <div>
                            <label className="uppercase-label block mb-1.5 text-[10px] text-slate-500 font-semibold">Send feedback to supervisor</label>
                            <textarea
                                value={feedback}
                                onChange={(e) => setFeedback(e.target.value)}
                                placeholder="e.g. Pulley replaced, running test now."
                                rows={2}
                                className="g-input"
                                data-testid="millwright-feedback-input"
                            />
                            <button
                                onClick={sendFeedback}
                                disabled={busy || !feedback.trim()}
                                className="btn-outline text-xs mt-2 inline-flex items-center gap-1 disabled:opacity-40"
                                data-testid="millwright-feedback-send-btn"
                            >
                                <ChatCircleText size={13} /> Post update
                            </button>
                        </div>
                    )}

                    <div>
                        <div className="uppercase-label text-[10px] text-slate-500 font-semibold mb-2">Updates ({(n.feedback || []).length})</div>
                        {(!n.feedback || n.feedback.length === 0) ? (
                            <div className="text-xs text-slate-400 italic">No updates yet.</div>
                        ) : (
                            <ul className="space-y-2" data-testid="millwright-feedback-list">
                                {[...n.feedback].reverse().map((f) => (
                                    <li key={f.id} className="border-l-2 border-slate-300 pl-3 py-1" data-testid={`millwright-feedback-${f.id}`}>
                                        <div className="text-xs text-slate-500 flex items-center gap-2">
                                            <span className="uppercase font-semibold" style={{ color: f.kind === "ack" ? "var(--grindrod-green)" : f.kind === "eta" ? "#92400E" : "var(--grindrod-navy)" }}>{f.kind}</span>
                                            <span>·</span>
                                            <span>{f.by_name}</span>
                                            <span>·</span>
                                            <span className="tabular">{new Date(f.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                                        </div>
                                        <div className="text-sm text-slate-700 mt-0.5">{f.text}</div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
